import { Component, OnInit, AfterViewInit, OnChanges, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { languageService, languageSupport } from '@app/modules/common/language';
import { viewService } from '@app/modules/view';

import { UiPrinterDevices } from '../devices/ui-printer/ui-printer';
import { UiScaleDevices } from '../devices/ui-scale/ui-scale';
import { UiSmartDrawerDevices } from '../devices/ui-smartdrawer/ui-smartdrawer';
import { UiPaymentTerminalDevices } from '../devices/ui-payment-terminal/ui-payment-terminal';

/************************************/
/* USER COMPONENT                   */
/************************************/

@Component({
    selector: 'ui-rasppi-devices',
    templateUrl: 'ui-devices.html',
    styleUrls: [
      'ui-devices.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiRaspPIDevices extends languageSupport  implements OnInit, AfterViewInit, OnChanges, OnDestroy { 
    @Input('service') _service = null;

    get MoreDevices(){
        if (!this.HasPrinters){
            return true;
        }

        if (!this.HasScales){
            return true;
        }

        if (!this.HasSmartDrawers){
            return true;
        }

        if (!this.HasTerminals){
            return true;
        }

        return false;   // all devices are available
    }

    private _printers: boolean = false;
    get TogglePrinters(){
        return this._printers;        
    }

    set TogglePrinters(value){
        this._printers = value;
        this.change.markForCheck();
    }

    private _scales: boolean = false;
    get ToggleScales(){
        return this._scales;
    }

    set ToggleScales(value){
        this._scales = value;
        this.change.markForCheck();
    }

    private _smartdrawers: boolean = false;
    get ToggleSmartDrawers(){
        return this._smartdrawers;
    }

    set ToggleSmartDrawers(value){
        this._smartdrawers = value;
        this.change.markForCheck();
    }

    private _terminals: boolean = false;
    get ToggleTerminals(){
        return this._terminals;
    }

    set ToggleTerminals(value){
        this._terminals = value;
        this.change.markForCheck();
    }

    @Output('OnChange') _onchange = new EventEmitter <any> ();
    OnViewChanged(){
        this.change.detectChanges();    // force view update 
        this._onchange.emit();
    }

    get IsValid(){        
        if (!this._printerctrl || !this._printerctrl.IsValid){
            return false;
        }

        if (!this._scalesctrl || !this._scalesctrl.IsValid){
            return false;
        }

        if (!this._smartdrawersctrl || !this._smartdrawersctrl.IsValid){
            return false;
        }

        if (this._terminalsctrl && !this._terminalsctrl.IsValid){
            return false;
        }

        return true;
    }

    get IsDirty(){
        if (!this.IsValid){
            return false;   // do not consider if its not valid
        }

        let _isdirty = false;

        _isdirty = _isdirty || this._printerctrl.IsDirty
        _isdirty = _isdirty || this._scalesctrl.IsDirty
        _isdirty = _isdirty || this._smartdrawersctrl.IsDirty

        if (this._terminalsctrl){
            _isdirty = _isdirty || this._terminalsctrl.IsDirty
        }

        return _isdirty;
    }

    set IsDirty(value){
        if (this._printerctrl){
            this._printerctrl.IsDirty = false;
        }        

        if (this._scalesctrl){
            this._scalesctrl.IsDirty = false;
        }

        if (this._smartdrawersctrl){
            this._smartdrawersctrl.IsDirty = false;
        }

        if (this._terminalsctrl){
            this._terminalsctrl.IsDirty = false;
        }
    }

    private _editdevice = null;
    get EditDevice(){
        return this._editdevice;
    }

    set EditDevice(value){
        if (value != this._editdevice){
            this._editdevice = value;
        }

        this._onchange.next();
    }

    private _lastdevice = null;
    get LastDevice(){
        return this._lastdevice;
    }

    set LastDevice(value){
        this._lastdevice = value;
    }

    /****************************/
    /* STARTS HERE              */
    /****************************/

    private _service_subscription = null;

    constructor(private lang: languageService, private change: ChangeDetectorRef, public view: viewService) {
        super(lang, change);
    }

    ngOnInit(){
        // nothing to do
    }

    ngOnChanges(){
        if (this._service_subscription){
            this._service_subscription.unsubscribe();
            this._service_subscription = null;
        }

        this._service_subscription = this._service.OnRefresh.subscribe(
        data => {
            this.change.markForCheck();
        });

        this.OnViewChanged();
    }

    private _OnControlsLoaded(){
        if (this._printerctrl && this._scalesctrl && this._terminalsctrl){
            this.change.markForCheck();
        }
        else {
            setTimeout(() => {
                this._OnControlsLoaded()
            }, 150);
        }
    }

    ngAfterViewInit(){
        this._OnControlsLoaded();
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._service_subscription){
            this._service_subscription.unsubscribe();
            this._service_subscription = null;
        }        
    }

    /****************************/
    /* COMMON METHODS           */
    /****************************/

    DiscardChanges(){
        if (this._printerctrl){
            this._printerctrl.DiscardChanges();
        }

        if (this._scalesctrl){
            this._scalesctrl.DiscardChanges();
        }

        if (this._smartdrawersctrl){
            this._smartdrawersctrl.DiscardChanges();
        }

        if (this._terminalsctrl){
            this._terminalsctrl.DiscardChanges();
        }
    }

    ApplyChanges(){
        this._ApplyPrinters();
        this._ApplyScales();
        this._ApplySmartDrawers();
        this._ApplyTerminals();

        this.IsDirty = false;
    }

    /****************************/
    /* EXPAND CONTROL           */
    /****************************/

    private _expand = false;
    get ExpandDevices(){
        return this._expand;
    }

    @ViewChild('toggles', { static: false }) _toggles: ElementRef;
    OnExpandDevices(){
        this._expand = !this._expand;

        if (this._toggles){
            let _toggles = this._toggles.nativeElement;
            if (this._expand){
                _toggles.style.height = _toggles.scrollHeight + 'px'; 
                setTimeout(() => {
                    _toggles.style.height = 'auto';
                }, 200);                
            }
            else {
                _toggles.style.height = _toggles.scrollHeight + 'px';
                _toggles.offsetHeight;                
                _toggles.style.height = '0px';     
            }    
        }

        this.change.markForCheck();
    }

    get HasPrinters(){
        if (this._printerctrl){
            return (this._printerctrl.PlacePrinters.length > 0);
        }

        return false;
    }

    get HasScales(){
        if (this._scalesctrl){
            return (this._scalesctrl.PlaceScales.length > 0);
        }

        return false;
    }

    get HasTerminals(){
        if (this._terminalsctrl){
            return (this._terminalsctrl.PlaceTerminals.length > 0);
        }

        return false;
    }

    get HasSmartDrawers(){
        if (this._smartdrawersctrl){
            return (this._smartdrawersctrl.PlaceSmartDrawers.length > 0);
        }
        
        return false;
    }

    /****************************/
    /* PRINTER DEVICES          */
    /****************************/

    @ViewChild('printers', { static: false }) _printerctrl: UiPrinterDevices;
    OnPrintersUpdate(){
        if (this._printerctrl) {
            let _editdevice = this._printerctrl._editdevice;
            if (_editdevice != this.EditDevice){
                this.LastDevice = this.EditDevice;
                this.EditDevice = _editdevice;
            }
        }

        this.change.markForCheck();
    }

    get DeviceProducts(){
        if (this._printerctrl) {
            return this._printerctrl.PrinterProducts;
        }

        return null;
    }

    OnAddProducts(products){
        if (this._printerctrl) {
            this._printerctrl.OnAddProducts(products);
        }

        this.change.markForCheck();
    }

    OnHideProducts(){
        if (this._printerctrl) {
            this._printerctrl.OnHideProducts();
        }

        this.change.markForCheck();
    }

    private get _PlacePrinters(){
        if (this._printerctrl){
            return this._printerctrl.PlacePrinters;
        }
    }

    private _ApplyPrinters(){  
        let _printers = this._PlacePrinters;

        // include all printers in the final list
        for(let _printer of _printers){
            if (!this._service.printers.includes(_printer)){
                this._service.AddPrinter(_printer);
            }
        }

        // remove printers not in the final list
        for(let _printer of this._service.printers){
            if (!_printers.includes(_printer)){
                _printer.status = 'DE';
            }
        }
    }

    /****************************/
    /* SCALE DEVICES            */
    /****************************/

    @ViewChild('scales', { static: false }) _scalesctrl: UiScaleDevices;
    OnScalesUpdate(){
        if (this._scalesctrl) {
            let _editdevice = this._scalesctrl._editdevice;
            if (_editdevice != this.EditDevice){
                this.LastDevice = this.EditDevice;
                this.EditDevice = _editdevice;
            }
        }

        this.change.markForCheck();
    }

    private get _PlaceScales(){
        if (this._scalesctrl){
            return this._scalesctrl.PlaceScales;
        }
    }

    private _ApplyScales(){
        let _scales = this._PlaceScales;

        // include all scales in the final list
        for(let _scale of _scales){
            if (this._service.scales.indexOf(_scale) == -1){
                this._service.AddScale(_scale);
            }
        }

        // remove scales not in the final list
        for(let _scale of this._service.scales){
            if (_scales.indexOf(_scale) == -1){
                _scale.status = 'DE';
            }
        }
    }

    /****************************/
    /* SMART DRAWER DEVICES     */
    /****************************/

    @ViewChild('smartdrawers', { static: false }) _smartdrawersctrl: UiSmartDrawerDevices;
    OnSmartDrawersUpdate(){
        if (this._smartdrawersctrl) {
            let _editdevice = this._smartdrawersctrl._editdevice;
            if (_editdevice != this.EditDevice){
                this.LastDevice = this.EditDevice;
                this.EditDevice = _editdevice;
            }
        }

        this.change.markForCheck();
    }

    /****************************/
    /* PAYMENT TERMINAL DEVICES */
    /****************************/

    @ViewChild('terminals', { static: false }) _terminalsctrl: UiPaymentTerminalDevices;
    OnTerminalsUpdate(){
        if (this._terminalsctrl) {
            let _editdevice = this._terminalsctrl._editdevice;
            if (_editdevice != this.EditDevice){
                this.LastDevice = this.EditDevice;
                this.EditDevice = _editdevice;
            }
        }

        this.change.markForCheck();
    }

    private get _PlaceSmartDrawers(){
        if (this._smartdrawersctrl){
            return this._smartdrawersctrl.PlaceSmartDrawers;
        }
    }

    private _ApplySmartDrawers(){
        let _smartdrawers = this._PlaceSmartDrawers;

        // include all smart drawers in the final list. BaseObject ya
        // setea _toInsert=true cuando objid es null en el constructor,
        // asi que no hay que forzar el flag privado (era brittle).
        for(let _smartdrawer of _smartdrawers){
            if (this._service.smartdrawers.indexOf(_smartdrawer) == -1){
                this._service.AddSmartDrawer(_smartdrawer);
            }
        }

        // remove smart drawers not in the final list
        for(let _smartdrawer of this._service.smartdrawers){
            if (_smartdrawers.indexOf(_smartdrawer) == -1){
                _smartdrawer.status = 'DE';
            }
        }
    }

    private get _PlaceTerminals(){
        if (this._terminalsctrl){
            return this._terminalsctrl.PlaceTerminals;
        }
    }

    private _ApplyTerminals(){
        let _terminals = this._PlaceTerminals;
        if (!_terminals) return;

        // include all terminals in the final list
        for(let _terminal of _terminals){
            if (this._service.payment_terminals.indexOf(_terminal) == -1){
                this._service.AddPaymentTerminal(_terminal);
            }
        }

        // remove terminals not in the final list
        for(let _terminal of this._service.payment_terminals){
            if (_terminals.indexOf(_terminal) == -1){
                _terminal.status = 'DE';
            }
        }
    }
}